import normalizeText from '@/lib/normalizeText.js';

const toDisplayUserLabel = (userId) => {
  const normalizedUserId = normalizeText(userId);

  if (!normalizedUserId) {
    return 'Unknown user';
  }

  if (normalizedUserId.includes('@')) {
    return normalizedUserId.split('@')[0];
  }

  return normalizedUserId;
};

const toUserInitials = (value) => {
  const safeValue = normalizeText(value);

  if (!safeValue) {
    return '?';
  }

  const tokenizedValue = safeValue.includes('@') ? safeValue.split('@')[0] : safeValue;
  let tokens = tokenizedValue
    .replace(/[_-]+/gu, ' ')
    .split(/\s+/u)
    .map((token) => normalizeText(token))
    .filter(Boolean);

  if (tokens.length > 2) {
    tokens = tokens.filter((token) => {
      return /^[a-zA-Z0-9.]+$/u.test(token);
    });
  }

  if (tokens.length >= 2) {
    const firstToken = tokens[0].replace(/[^a-zA-Z0-9]/gu, '');
    const lastToken = tokens[tokens.length - 1].replace(/[^a-zA-Z0-9]/gu, '');

    if (firstToken && lastToken) {
      return `${firstToken.slice(0, 1)}${lastToken.slice(0, 1)}`.toUpperCase();
    }
  }

  if (tokens.length === 1) {
    const singleToken = tokens[0].replace(/[^a-zA-Z0-9]/gu, '');

    if (singleToken.length >= 2) {
      return `${singleToken.slice(0, 1)}${singleToken.slice(1, 2)}`.toUpperCase();
    }

    if (singleToken.length === 1) {
      return singleToken.slice(0, 1).toUpperCase();
    }
  }

  return '?';
};

const getAppLabelFromOperation = (operation = {}) => {
  return normalizeText(operation?.appName || operation?.appSlug || operation?.appId) || 'Unknown app';
};

const getAppFilterKey = (operation = {}) => {
  return normalizeText(operation?.appId || operation?.appSlug || operation?.appName);
};

const getUserFilterKey = (operation = {}) => {
  return normalizeText(operation?.userId);
};

const getOperationEntityLabel = (operation = {}) => {
  const entityTypeLabel = normalizeText(operation?.entityTypeLabel);
  const entityDisplay = normalizeText(operation?.entityDisplay);
  const entityId = normalizeText(operation?.entityId);

  if (entityDisplay && entityId && entityTypeLabel) {
    return `${entityTypeLabel}: ${entityDisplay} (ID ${entityId})`;
  }

  if (entityDisplay && entityId) {
    return `${entityDisplay} (ID ${entityId})`;
  }

  if (entityDisplay && entityTypeLabel) {
    return `${entityTypeLabel}: ${entityDisplay}`;
  }

  if (entityDisplay) {
    return entityDisplay;
  }

  if (entityId && entityTypeLabel) {
    return `${entityTypeLabel} (ID ${entityId})`;
  }

  if (entityId) {
    return `Record (ID ${entityId})`;
  }

  return normalizeText(operation?.entityTypeLabel || operation?.actionName || operation?.actionId || operation?.operationId) || 'Record';
};

const getOperationActivitySummary = (operation = {}, verb = '') => {
  const summary = normalizeText(operation?.summary);
  const entityId = normalizeText(operation?.entityId);
  const entityIdSuffix = entityId ? `(ID ${entityId})` : '';

  if (summary && entityIdSuffix && !summary.endsWith(entityIdSuffix)) {
    return `${summary} ${entityIdSuffix}`;
  }

  if (summary) {
    return summary;
  }

  const normalizedVerb = normalizeText(verb);
  const entityLabel = getOperationEntityLabel(operation);

  return normalizedVerb ? `${normalizedVerb} ${entityLabel}` : entityLabel;
};

export { toDisplayUserLabel, toUserInitials, getAppLabelFromOperation, getAppFilterKey, getUserFilterKey, getOperationEntityLabel, getOperationActivitySummary };
